import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  inject,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from "@angular/forms";

import { FormInputType } from "./form-input.model";
import { TranslateModule } from "@ngx-translate/core";
import { formatInternationalPhoneNumber } from "../../../../functions/common-functions";

@Component({
  selector: "app-form-input",
  templateUrl: "./form-input.component.html",
  styleUrls: ["./form-input.component.scss"],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormInputComponent),
      multi: true,
    },
  ],
})
export class FormInputComponent implements ControlValueAccessor, OnInit {

  @ViewChild('txtAreaInput') txtAreaInput: ElementRef<HTMLInputElement>;
  @ViewChild('defaultInput') defaultInput: ElementRef<HTMLInputElement>;

  @Input() placeholder = "";
  @Input() label = "";
  @Input() hint = "";
  @Input() type: FormInputType = FormInputType.Text;
  @Input() disabled = false;
  @Input() value: string | number | null = "";
  @Input() required: boolean = false;
  @Input() maxLength: number | null = null;
  @Input() currency: string | null = null;
  @Input() numericOnly: boolean = false;
  @Input() formatPhoneNumberOnBlur = false;

  @Output() valueChange = new EventEmitter<string | number | null>();
  @Output() inputBlurWithValueChange = new EventEmitter<string | number | null>();

  @HostBinding("class.input--focused")
  focused = false;

  FormInputType = FormInputType;

  private onChange = (_: any) => {};
  private onTouched = () => {};
  private injector = inject(Injector);
  private ngControl: NgControl | null;
  private previousValue: string | number = "";
  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null, {
      optional: true,
      self: true,
    });

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(obj: any): void {
    this.value = obj ?? null;
    if (this.type === FormInputType.Number && !Number.isNaN(Number(this.value))) {
      this.value = Number(this.value);
    }
    this.previousValue = this.value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  formatInputValue(value: string | number): string {

    if (value === null || value === undefined) {
      return "";
    }

    if (this.type === FormInputType.Number || this.type === FormInputType.NullableNumber) {
      value = value.toString();
      if (value) {
        return value.replace(".", ",");
      }
    }
    return value.toString();
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let displayValue: string | number | null = input.value;
    let modelValue = displayValue;

    if (this.type === FormInputType.Number || this.type === FormInputType.NullableNumber) {
      displayValue = displayValue
        .replace(/[^0-9,]/g, "")
        .replace(/^(\d*,?\d*).*$/, "$1");
      modelValue = displayValue.replace(",", ".");   
    } else if (this.numericOnly && this.type === FormInputType.Text) {
      // Filter non-numeric characters for text input when numericOnly is true
      displayValue = (displayValue as string).replace(/[^0-9]/g, "");
      modelValue = displayValue;
    }

    // Enforce maxLength constraint
    if (this.maxLength !== null && (displayValue as string).length > this.maxLength) {
      displayValue = (displayValue as string).substring(0, this.maxLength);
      modelValue = displayValue;
    }

    if (this.type === FormInputType.Number && !Number.isNaN(Number(modelValue))) {
      this.value =  modelValue ? Number(modelValue) : null;
    } else {
      this.value = displayValue;
    }
    
    input.value = displayValue as string;
    this.onChange(modelValue);
    this.valueChange.emit(modelValue as string);
  }

  onBlur(): void {
    this.focused = false;

    if (this.formatPhoneNumberOnBlur && typeof this.value === "string") {
      const formattedValue = formatInternationalPhoneNumber(this.value);
      if (formattedValue !== null && formattedValue !== this.value) {
        this.value = formattedValue;
        if (this.defaultInput) {
          this.defaultInput.nativeElement.value = formattedValue;
        }
        this.onChange(formattedValue);
        this.valueChange.emit(formattedValue);
      }
    }

    this.onTouched();

    if (this.value !== this.previousValue) {
      this.inputBlurWithValueChange.emit(this.value);
      this.previousValue = this.value;
    }
  }

  onFocus(): void {
    this.focused = true;
    if (
      this.type === FormInputType.Number ||
      this.type === FormInputType.NullableNumber
    ) {
      this.selectAllCurrentInputValue();
    }
  }

  private selectAllCurrentInputValue() {
    const inputElement = this.defaultInput?.nativeElement;
    if (!inputElement) {
      return;
    }

    setTimeout(() => {
      const length = inputElement.value?.length ?? 0;
      inputElement.setSelectionRange(0, length);
    }, 0);
  }

  public backToInput() {
    
    setTimeout(() => {

      const txtAreaInputElem = this.txtAreaInput?.nativeElement;
      const defaultInputElem = this.defaultInput?.nativeElement;

      const el = txtAreaInputElem ? txtAreaInputElem : defaultInputElem ? defaultInputElem : null;
      if (!el) return;

      setTimeout(() => {
        el.focus();
        const {length} = el.value;
        el.setSelectionRange(length, length);
      }, 0);
    });
  }

  get isOverMaxLength() {
    if (this.maxLength && this.value !== null && this.value.toString().split("").length > this.maxLength) {
      return true;
    } else {
      return false;
    }
  }

  get isRequired(): boolean {
    const validator = this.ngControl?.control?.validator;
    if (!validator) {
      return this.required;
    }

    const result = validator({} as any);
    return result?.required === true;
  }
}
